import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardContent, Grid, Typography } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { Autocomplete, CustomTextField, DateTime } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, set } from "@totalsoft/change-tracking-react";
import { getErrors, isValid } from "@totalsoft/pure-validations";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { emptyString } from "constants/defaultValues";
import { getTenant } from "providers/TenantProvider";
import { countryCodeConstants, legalFormCodes } from "features/common/constants";
import omitDeep from "omit-deep-lodash";
import { initialPerson } from "features/client/constants";
import { useStateLens } from "@totalsoft/react-state-lens";
import AddressData from "./AddressData";

function ClientSoleTraderFeature({
  selectedClientLens,
  legalFormValues,
  areaOfActivities,
  countries,
  counties,
  validation,
  onBack,
}) {
  const { t } = useTranslation();
  const { countryCode } = getTenant();
  const isPartnershipLens = useStateLens(false);
  const isPartnership = isPartnershipLens |> get;
  const selectedClient = selectedClientLens |> get;

  const newPerson = useMemo(() => {
    return {
      ...initialPerson,
      countryId: selectedClient.countryId,
      nationality:
        countryCode === countryCodeConstants.unitedKingdom
          ? t("Person.Nationalities.UnitedKingdom")
          : t("Person.Nationalities.Ireland"),
    };
  }, [countryCode, selectedClient.countryId, t]);

  const onChangeLegalForm = useCallback(
    (id) => {
      if (selectedClient.legalFormId === id) return;
      var currentLegalForm = legalFormValues.find((l) => l.id === id);
      (selectedClientLens.legalFormId |> set)(id);
      (selectedClientLens.legalFormCode |> set)(currentLegalForm?.code);

      const isPartnership = currentLegalForm?.code === legalFormCodes.partnership;
      if (isPartnership) {
        (selectedClientLens |> set)({
          ...selectedClient,
          legalFormId: id,
          legalFormCode: legalFormCodes.partnership,
          persons: [newPerson, newPerson],
        });
        (isPartnershipLens |> set)(true);
      } else {
        (isPartnershipLens |> set)(false);
        if (Object.keys(selectedClient).includes("persons")) {
          (selectedClientLens |> set)({
            ...omitDeep(selectedClient, ["persons"]),
            legalFormId: id,
            legalFormCode: currentLegalForm?.code,
          });
        }
      }
    },
    [selectedClientLens, selectedClient, legalFormValues, newPerson, isPartnershipLens]
  );

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h4">{t("Client.CompanyData")}</Typography>
          <Grid container>
            <Grid item container spacing={2}>
              <Grid item xs={12} sm={3}>
                <CustomTextField
                  required
                  fullWidth
                  label={t("Client.CompanyName")}
                  value={(selectedClientLens.companyName |> get) || emptyString}
                  onChange={selectedClientLens.companyName |> set |> onTextBoxChange}
                  error={!isValid(validation.companyName)}
                  helperText={getErrors(validation.companyName)}
                />
              </Grid>
              {!isPartnership && (
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    required
                    fullWidth
                    label={t("Client.RegistrationNumber")}
                    value={(selectedClientLens.registrationNumber |> get) || emptyString}
                    onChange={selectedClientLens.registrationNumber |> set |> onTextBoxChange}
                    error={!isValid(validation.registrationNumber)}
                    helperText={getErrors(validation.registrationNumber)}
                  />
                </Grid>
              )}
              {isPartnership && (
                <Grid item xs={12} sm={3}>
                  <DateTime
                    fullWidth
                    disableToolbar={false}
                    label={t("Client.RegistrationDate") + " *"}
                    value={selectedClientLens.registrationDate |> get}
                    onChange={selectedClientLens.registrationDate |> set}
                    error={!isValid(validation.registrationDate)}
                    helperText={getErrors(validation.registrationDate)}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  label={t("Client.LegalForm") + " *"}
                  noOptionsText={t("General.AutocompleteNoOptions")}
                  simpleValue={true}
                  isClearable={true}
                  valueKey="id"
                  labelKey="name"
                  options={legalFormValues}
                  onChange={onChangeLegalForm}
                  value={(selectedClientLens.legalFormId |> get) || emptyString}
                  error={!isValid(validation.legalFormId)}
                  helperText={getErrors(validation.legalFormId)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  label={t("Client.AreaOfActivity") + (isPartnership ? " *" : "")}
                  noOptionsText={t("General.AutocompleteNoOptions")}
                  simpleValue={true}
                  isClearable={true}
                  valueKey="id"
                  labelKey="name"
                  options={areaOfActivities}
                  onChange={selectedClientLens.areaOfActivityId |> set}
                  value={(selectedClientLens.areaOfActivityId |> get) || emptyString}
                  error={!isValid(validation.areaOfActivityId)}
                  helperText={getErrors(validation.areaOfActivityId)}
                />
              </Grid>
              <AddressData
                addressLens={selectedClientLens}
                countries={countries}
                counties={counties}
                validation={validation}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <CustomTextField
                fullWidth
                label={t("Person.Neighborhood")}
                value={(selectedClientLens.neighborhood |> get) || emptyString}
                onChange={selectedClientLens.neighborhood |> set |> onTextBoxChange}
              />
            </Grid>
          </Grid>
          <Grid item container justifyContent="flex-end">
            <Button
              id="createClient_BackToSearchBtn"
              right="true"
              variant="contained"
              color="primary"
              onClick={onBack}
              endIcon={<ArrowBack />}
            >
              {t("General.BackToSearch")}
            </Button>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

ClientSoleTraderFeature.propTypes = {
  selectedClientLens: PropTypes.object.isRequired,
  legalFormValues: PropTypes.array.isRequired,
  areaOfActivities: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  counties: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ClientSoleTraderFeature;
